// ***********************************
// MAT (Migrations Acceleration Toolkit) CSV export
// ***********************************
//
// Prototype mapping of DBCSI data into MAT's oracle-inventory-template.csv format.
// Columns DBCSI does not collect are left empty, except columns MAT requires, which get a placeholder (see MAT_PLACEHOLDER).
// Some columns (tablesWithoutPk, jobCount, clusterFreeMemoryGb, redo rates, Data Guard) are only collected by
// aws_awr_miner.sql 4.0.11.aws.65+ / aws_statspack_miner.sql 4.0.11.statspack.34+, so older collections get the placeholder.

// Column order of MAT's oracle-inventory-template.csv
const MAT_FIELDS = [
  "dbIdentifier", "clusterName", "cdbName", "sourceEngine", "migrationSchemas",
  "dbEngineVersion", "dbEngineEdition", "isRacCluster", "nodeCount", "pdbCount",
  "sgaPgaTotalGb", "storageAllocatedGb", "storageUsedGb", "tableCount", "viewCount",
  "indexCount", "storedProcCount", "functionCount", "triggerCount", "trustworthyProperty",
  "tablesWithoutPk", "featureUsage", "dbLinkCount", "saPrivilegeCount", "jobCount",
  "biComponents", "haSetup", "eeFeatureUsage", "clusterVcpuCount", "clusterTotalMemoryGb",
  "clusterFreeMemoryGb", "clusterTotalStorageGb", "clusterCpuAvgP99", "clusterCpuMaxP90",
  "clusterIopsAvgP99", "clusterIopsMaxP50", "clusterThroughputAvgP99", "clusterThroughputMaxP50",
  "clusterNetThroughputAvg", "clusterNetThroughputMax", "dbCpuAvgP99", "dbCpuMaxP90",
  "dbIopsAvgP99", "dbIopsMaxP50", "dbThroughputAvgP99", "dbThroughputMaxP50",
  "dbRedoRateAvgP99", "dbRedoRateMaxP50", "isIsvCotsApplication", "isCompatibleWithMinimumVersion",
  "isCOTSApplicationCompatibleWithMinimumSupportedVersion", "osLevelAccessRequired",
  "systemAccessRequired", "preferLicenseIncluded", "optimiseForLicense", "isReadReplicaRequired",
  "readReplicaCount", "preferEnterpriseEdition", "minimalCutoverDowntimeRequired",
  "preferGoldenGate", "requireMultiAZ", "targetAWSRegion", "Environment",
  "UserExtra", "UserExtra2", "UserExtra3", "UserExtra4", "UserExtra5" // not MAT template columns; DBCSI-added for traceability
];

// The DBA_FEATURE_USAGE_STATISTICS names MAT's oracle_database_features_collection.sql reports in featureUsage
const MAT_FEATURE_NAMES = [
  "Active Data Guard",
  "Active Data Guard - Real-Time Query on Physical Standby",
  "Advanced Compression",
  "Advanced Replication",
  "Advanced Security",
  "Application Express",
  "Data Guard",
  "Data Mining",
  "Data Redaction",
  "Database Vault",
  "Diagnostics Pack",
  "Editioning Views",
  "Exadata",
  "Fine Grained Audit",
  "GoldenGate",
  "Hybrid Columnar Compression",
  "In-Memory Column Store",
  "In-Memory Database",
  "Java Virtual Machine (user)",
  "Label Security",
  "Multitenant",
  "Online Redefinition",
  "One Node",
  "Oracle In-Database Hadoop",
  "Oracle Multitenant",
  "Partitioning",
  "Partitioning (user)",
  "Parallel SQL Query Execution",
  "RAC",
  "Real Application Cluster One Node",
  "Real Application Clusters",
  "Real Application Clusters (RAC)",
  "Real Application Security",
  "Resource Manager",
  "Shard Database",
  "Spatial",
  "Transparent Data Encryption",
  "Tuning Pack",
  "Virtual Private Database (VPD)"
];

// eeFeatureUsage label -> feature usage names that trigger it (from MAT's features script)
const MAT_EE_FEATURES = [
  { label: "Partitioning", names: ["Partitioning", "Partitioning (user)"] },
  { label: "Advanced Compression", names: ["Advanced Compression"] },
  { label: "Parallel SQL Query Execution", names: ["Parallel SQL Query Execution"] },
  { label: "Active Data Guard", names: ["Active Data Guard", "Active Data Guard - Real-Time Query on Physical Standby"] },
  { label: "Transparent Data Encryption", names: ["Transparent Data Encryption"] },
  { label: "Label Security", names: ["Label Security"] },
  { label: "Database Vault", names: ["Database Vault"] },
  { label: "In-Memory Column Store", names: ["In-Memory Column Store", "In-Memory Database"] },
  { label: "Online Redefinition", names: ["Online Redefinition"] },
  { label: "Fine Grained Audit", names: ["Fine Grained Audit"] },
  { label: "Virtual Private Database (VPD)", names: ["Virtual Private Database (VPD)"] },
  { label: "Advanced Replication", names: ["Advanced Replication"] },
  { label: "Spatial", names: ["Spatial"] },
  { label: "Data Mining", names: ["Data Mining"] },
  { label: "Real Application Security", names: ["Real Application Security"] },
  { label: "Diagnostics Pack", names: ["Diagnostics Pack"] },
  { label: "Tuning Pack", names: ["Tuning Pack"] }
];

// Placeholder for columns MAT requires but DBCSI does not collect yet.  MAT rejects empty values for these.
const MAT_PLACEHOLDER = 0;

// ***********************************
// helpers
// ***********************************

function isBlank(value) {
  return value === undefined || value === null || value === "" || (typeof value === "number" && isNaN(value));
}

// round a numeric value, or return "" if it is missing
function num(value, decimals) {
  if (isBlank(value) || isNaN(value * 1)) return "";
  return (value * 1).toFixed(decimals) * 1;
}

// first non-blank value
function firstOf(...values) {
  for (const v of values) {
    if (!isBlank(v)) return v;
  }
  return "";
}

// The upload lambda truncates metric names to 60 chars, so "FEATURE_<name>" keys can be cut short.
// Map each FEATURE_ key in genInfo back to its full MAT feature name.
function getFeaturesInUse(genInfo) {
  const inUse = new Set();
  for (const key of Object.keys(genInfo)) {
    if (!key.startsWith("FEATURE_") || genInfo[key] !== "Yes") continue;
    const shortName = key.substring(8);
    for (const name of MAT_FEATURE_NAMES) {
      if (name === shortName || (key.length >= 58 && name.startsWith(shortName))) {
        inUse.add(name);
      }
    }
  }
  return inUse;
}

// Plain-text version of the scenario's assumptions, cleaned up the same way as the DETAIL CSV's Assumptions column
function getAssumptionsText(assumptionsString) {
  if (isBlank(assumptionsString)) return "";
  return assumptionsString.replace(/<br\/>/g, "").replace(/<b>/g, "").replace(/<\/b>/g, "")
    .replace(/&lt;/g, "<").replace(/&amp;/g, "&").trim();
}

function getEdition(banner) {
  if (isBlank(banner)) return "";
  if (banner.includes("Enterprise Edition")) return "Enterprise Edition";
  if (banner.includes("Standard Edition 2")) return "Standard Edition 2";
  if (banner.includes("Standard Edition")) return "Standard Edition";
  return "Unknown Edition";
}

/*

Summary:
The function `build_mat_row` converts one DBCSI batch item into a row of MAT's oracle-inventory-template.csv.

Description:
1. Builds the MAT identity columns (CDB/PDB naming) from DB_NAME and PDB_NAME
2. Maps DBCSI general info (version, edition, object counts, storage, features) to MAT columns
3. Maps DBCSI percentiles to MAT's performance columns. DBCSI's read+write totals are used for IOPS and throughput.
4. Fills the questionnaire columns DBCSI knows from the scenario (region, Multi-AZ, license optimization)
5. Leaves columns DBCSI does not collect empty, or uses a placeholder where MAT requires a value

Parameters:
1. item (object): the batchItems row (includes additional_datapoints, frozen_prefs, UseMAZ)
2. genInfo (object): name/value map of the collection's general info (GET /dbcsi/:id/ora_v1)
3. cdbItem (object, optional): the batchItems row of this PDB's CDB, used for pdbCount
4. regionCode (string): the target AWS region

Returns an object with one property per entry in MAT_FIELDS.

*/

function build_mat_row(item, genInfo, cdbItem, regionCode) {
  const g = genInfo || {};
  const adp = item.additional_datapoints || {};
  const isPDB = (item.IS_PDB == "YES");
  const numInstances = Math.max(1, firstOf(item.NUM_INSTANCES, g.INSTANCES, 1) * 1);

  // identity: MAT uses CDBNAME/PDBNAME for PDBs and NAME/NAME otherwise.  DBCSI stores a PDB's DB_NAME as CDBNAME-PDBNAME.
  const dbName = firstOf(g.DB_NAME, item.database);
  let cdbName = dbName;
  let dbIdentifier = dbName + "/" + dbName;
  if (isPDB && !isBlank(g.PDB_NAME) && dbName.endsWith("-" + g.PDB_NAME)) {
    cdbName = dbName.substring(0, dbName.length - g.PDB_NAME.length - 1);
    dbIdentifier = cdbName + "/" + g.PDB_NAME;
  }

  // pdbCount: a PDB collection only sees itself, so take the count from its CDB when the CDB is in the batch
  let pdbCount = "";
  if (isPDB) {
    if (cdbItem && !isBlank(cdbItem.COUNT_PDBS) && cdbItem.COUNT_PDBS * 1 >= 0) pdbCount = cdbItem.COUNT_PDBS * 1;
  } else if (!isBlank(item.COUNT_PDBS) && item.COUNT_PDBS * 1 >= 0) {
    pdbCount = item.COUNT_PDBS * 1;
  }

  // object counts: the miner only emits COUNT_<type> rows for types that exist, so missing means 0 when the object section ran
  const hasCounts = Object.keys(g).some(k => k.startsWith("COUNT_"));
  const count = (key) => (!isBlank(g[key]) ? g[key] * 1 : (hasCounts ? 0 : ""));

  // features
  const featuresInUse = getFeaturesInUse(g);
  const featureUsage = [...featuresInUse].sort();

  const isStandby = !isBlank(item.DATABASE_ROLE) && item.DATABASE_ROLE.includes("STANDBY");
  const usesADG = featuresInUse.has("Active Data Guard") || featuresInUse.has("Active Data Guard - Real-Time Query on Physical Standby");

  // MAT reports Data Guard for a standby, or when V$DATAGUARD_CONFIG has rows.  Older miner versions did not collect
  // DATAGUARD_CONFIG_COUNT, so for those use the Data Guard feature or a standby role instead.
  const hasDgCount = !isBlank(g.DATAGUARD_CONFIG_COUNT) && !isNaN(g.DATAGUARD_CONFIG_COUNT * 1);
  const usesDataGuard = isStandby || (hasDgCount ? g.DATAGUARD_CONFIG_COUNT * 1 > 0 : featuresInUse.has("Data Guard"));
  let haSetup = "None";
  if (numInstances > 1) haSetup = "RAC";
  else if (usesDataGuard) haSetup = "Data Guard";
  else if (usesADG) haSetup = "Active Data Guard";

  const eeFeatures = MAT_EE_FEATURES
    .filter(f => f.names.some(n => featuresInUse.has(n)))
    .map(f => f.label);
  if (!isBlank(g.COUNT_MATVIEWS_REWRITE_ENABLED) && g.COUNT_MATVIEWS_REWRITE_ENABLED * 1 > 0) eeFeatures.push("Materialized Views with Query Rewrite");
  if (usesDataGuard) eeFeatures.push("Data Guard");
  if (numInstances > 1) eeFeatures.push("Real Application Clusters (RAC)");
  if (!isPDB && pdbCount !== "" && pdbCount > 3) eeFeatures.push("Multitenant (>3 PDBs)");
  eeFeatures.sort();

  // performance.  db = this database (PDB values for a PDB).  cluster = host CPU; falls back to the db values when host CPU was not collected (e.g. STATSPACK)
  const dbCpuAvg = num(firstOf(adp.DB_CPU_THREADS_AVERAGE_P99, g.DB_CPU_THREADS_AVERAGE_P99), 3);
  // miner versions before 4.0.11.aws.65 named the CPU peak fields without the P (e.g. DB_CPU_THREADS_PEAKS_90)
  const dbCpuMax = num(firstOf(adp.DB_CPU_THREADS_PEAKS_P90, g.DB_CPU_THREADS_PEAKS_P90, g.DB_CPU_THREADS_PEAKS_90), 3);
  const dbIopsAvg = num(firstOf(adp.DB_IOPS_AVERAGE_P99, g.DB_IOPS_AVERAGE_P99), 0);
  const dbIopsMax = num(firstOf(adp.DB_IOPS_PEAKS_P50, g.DB_IOPS_PEAKS_P50), 0);
  const dbIombAvg = num(firstOf(adp.DB_IOMB_AVERAGE_P99, g.DB_IOMB_AVERAGE_P99), 3);
  const dbIombMax = num(firstOf(adp.DB_IOMB_PEAKS_P50, g.DB_IOMB_PEAKS_P50), 3);
  const hostCpuAvg = num(g.DB_HOST_CPU_THREADS_AVERAGE_P99, 3);
  const hostCpuMax = num(firstOf(g.DB_HOST_CPU_THREADS_PEAKS_P90, g.DB_HOST_CPU_THREADS_PEAKS_90), 3);
  const hasHostCpu = (hostCpuAvg !== "" && hostCpuAvg > 0);

  // values only collected by newer miner versions.  For older collections, MAT-required columns get MAT_PLACEHOLDER and are listed in UserExtra5.
  const hardCoded = ["Environment", "readReplicaCount"];
  const collectedOr = (value, column) => {
    if (value !== "") return value;
    hardCoded.push(column);
    return MAT_PLACEHOLDER;
  };
  const tablesWithoutPk = collectedOr(num(g.COUNT_TABLES_WITHOUT_PK, 0), "tablesWithoutPk");
  // MAT's jobCount is DBA_SCHEDULER_JOBS where ENABLED='TRUE', falling back to DBA_JOBS where BROKEN='N' only if the scheduler query fails
  const jobCount = collectedOr(num(firstOf(g.COUNT_SCHEDULER_JOBS_ENABLED, g.COUNT_DBMS_JOBS_NOT_BROKEN), 0), "jobCount");
  // MAT averages free memory across the RAC nodes (not a sum), and so does the miner's FREE_MEMORY_GB
  const clusterFreeMemoryGb = collectedOr(num(g.FREE_MEMORY_GB, 0), "clusterFreeMemoryGb");
  const dbRedoAvg = collectedOr(num(g.DB_REDO_MB_AVERAGE_P99, 3), "dbRedoRateAvgP99");
  // STATSPACK has no peak metrics, so dbRedoRateMaxP50 is only available from aws_awr_miner.sql
  const dbRedoMax = collectedOr(num(g.DB_REDO_MB_PEAKS_P50, 3), "dbRedoRateMaxP50");
  hardCoded.push("clusterNetThroughputAvg", "clusterNetThroughputMax");

  const optgoal = (item.frozen_prefs && item.frozen_prefs.optgoal_algo) || "";

  return {
    dbIdentifier: dbIdentifier,
    clusterName: cdbName,
    cdbName: cdbName,
    sourceEngine: "oracle",
    migrationSchemas: "",
    dbEngineVersion: firstOf(item.VERSION, g.VERSION),
    dbEngineEdition: getEdition(firstOf(item.BANNER, g.BANNER)),
    isRacCluster: (numInstances > 1) ? "Y" : "N",
    nodeCount: numInstances,
    pdbCount: pdbCount,
    sgaPgaTotalGb: num(firstOf(adp.DB_SGAPGA_TOTAL, g.DB_SGAPGA_TOTAL), 1),
    storageAllocatedGb: num(g.TOTAL_DATAFILE_SIZE_GB, 3),
    storageUsedGb: num(g.USED_DB_SIZE_GB, 3),
    tableCount: count("COUNT_TABLE"),
    viewCount: count("COUNT_VIEW"),
    indexCount: count("COUNT_INDEX"),
    storedProcCount: count("COUNT_PROCEDURE"),
    functionCount: count("COUNT_FUNCTION"),
    triggerCount: count("COUNT_TRIGGER"),
    trustworthyProperty: "N/A",
    tablesWithoutPk: tablesWithoutPk, // Note: MAT treats 0 as eligible for GoldenGate / DMS CDC, so the placeholder 0 for older collections is optimistic
    featureUsage: featureUsage.length > 0 ? featureUsage.join(", ") : "None",
    dbLinkCount: count("COUNT_DB_LINKS"),
    saPrivilegeCount: 0, // MAT's own collector always reports 0 (it looks for SYSDBA/SYSOPER in DBA_ROLE_PRIVS)
    jobCount: jobCount,
    biComponents: "",
    haSetup: haSetup,
    eeFeatureUsage: eeFeatures.length > 0 ? eeFeatures.join("; ") : "None",
    clusterVcpuCount: num(item.current_hw_cpu, 0),
    clusterTotalMemoryGb: isBlank(item.current_hw_memory) ? "" : Math.round(item.current_hw_memory * numInstances),
    clusterFreeMemoryGb: clusterFreeMemoryGb,
    clusterTotalStorageGb: num(g.TOTAL_DATAFILE_SIZE_GB, 0),
    clusterCpuAvgP99: hasHostCpu ? hostCpuAvg : dbCpuAvg,
    clusterCpuMaxP90: hasHostCpu ? hostCpuMax : dbCpuMax,
    clusterIopsAvgP99: dbIopsAvg,
    clusterIopsMaxP50: dbIopsMax,
    clusterThroughputAvgP99: dbIombAvg,
    clusterThroughputMaxP50: dbIombMax,
    clusterNetThroughputAvg: MAT_PLACEHOLDER, // not computed yet
    clusterNetThroughputMax: MAT_PLACEHOLDER, // not computed yet
    dbCpuAvgP99: dbCpuAvg,
    dbCpuMaxP90: dbCpuMax,
    dbIopsAvgP99: dbIopsAvg,
    dbIopsMaxP50: dbIopsMax,
    dbThroughputAvgP99: dbIombAvg,
    dbThroughputMaxP50: dbIombMax,
    dbRedoRateAvgP99: dbRedoAvg,
    dbRedoRateMaxP50: dbRedoMax,
    isIsvCotsApplication: "",
    isCompatibleWithMinimumVersion: "",
    isCOTSApplicationCompatibleWithMinimumSupportedVersion: "",
    osLevelAccessRequired: "",
    systemAccessRequired: "",
    preferLicenseIncluded: "",
    optimiseForLicense: optgoal == "min_license" ? "Y" : (optgoal == "min_cost" ? "N" : ""),
    isReadReplicaRequired: "",
    readReplicaCount: 0, // MAT requires an integer; 0 = no read replicas
    preferEnterpriseEdition: "",
    minimalCutoverDowntimeRequired: "",
    preferGoldenGate: "",
    requireMultiAZ: item.UseMAZ == "Yes" ? "Y" : (item.UseMAZ == "No" ? "N" : ""),
    targetAWSRegion: regionCode || "",
    Environment: "prod", // MAT only accepts prod or non-prod; DBCSI does not know, so assume prod
    UserExtra: "DBCSI collection id:" + item.collectionID,
    UserExtra2: (item.targetconfig_rds && !isBlank(item.targetconfig_rds.summaryText)) ? item.targetconfig_rds.summaryText : "", // RDS-Oracle Summary
    UserExtra3: firstOf(item.PLATFORM_NAME, g.PLATFORM_NAME),
    UserExtra4: ((regionCode ? "Region: " + regionCode + ". " : "") + getAssumptionsText(item.assumptionsString)).trim(),
    UserExtra5: "DBCSI does not know the following values and has hard-coded them: " + hardCoded.join(", ")
  };
}

export { MAT_FIELDS, build_mat_row };
